/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react';
import {
  formatPhoneNumber,
  formatTel,
  getAdobeFlowName,
  isCaremark,
  isSpecialty,
  onlyNumberKey
} from '../../lib/utils/utils';
import styles from './personalInfoForm.module.scss';
import { useRouter } from 'next/router';
import { Input } from '@digital-pulse/react';
import { formErrorAnalytics, tags } from '../../lib/utils/analytics';
import MemberIDModal from '../memberIDModal/memberIDModal';
import DateFieldsetWrapper from '@/components/dateFieldset/DateFieldsetWrapper';

interface PhoneNumber {
  number?: string;
  type?: string;
}

interface PersonalInfoProps {
  firstName?: string;
  lastName?: string;
  phoneNumber?: Array<PhoneNumber>;
  month?: string;
  day?: string;
  year?: string;
}
interface Props {
  personalInfoData?: PersonalInfoProps;
  formStatus?: any;
  dobHelper?: string;
  verificationHelper?: boolean;
  isUnifiedRegistration?: boolean;
  dobShowHide?: boolean;
  // pre-check the show/hide toggle so the dob starts unmasked; opt in per page
  dobShowHideChecked?: boolean;
  phoneDOB?: boolean;
  showMemberId?: boolean;
  isMobile?: boolean;
  translations?: any;
  englishMessageMap?: Record<string, string>;
  analytics?: { flowName: string; pageName: string };
}

const PersonalInfoForm: FC<PropsWithChildren<Props>> = ({
  personalInfoData,
  formStatus,
  isUnifiedRegistration = true,
  dobShowHide = !isUnifiedRegistration,
  dobShowHideChecked = false,
  phoneDOB = false,
  showMemberId,
  translations,
  englishMessageMap,
  analytics
}: Props): ReactElement => {
  const { register, formState, setValue, getValues } = formStatus;
  const isSpecialtyLob = isSpecialty();
  const formLabels = isSpecialtyLob
    ? { ...translations?.IDENTITY_INFO_FORM_LABELS, ...translations?.SPECIALTY_IDENTITY_INFO_FORM_LABELS }
    : translations?.IDENTITY_INFO_FORM_LABELS;
  const dobLegendLabel = isSpecialtyLob
    ? translations?.SPECIALTY_IDENTITY_INFO_FORM_LABELS?.DATE_OF_BIRTH
    : translations?.DOB_LABELS?.DATE_OF_BIRTH;
  const router = useRouter();
  const { errors } = formState;
  const phoneInputRef = useRef(null);
  const dobInputRef = useRef(null);
  const formDetails = {
    firstNameHelper: '',
    lastNameHelper: '',
    dateOfBirthHelper: translations?.IDENTITY_INFO_FORM_HELP_TEXT?.DATE_OF_BIRTH,
    dayHelper: translations?.IDENTITY_INFO_FORM_HELP_TEXT?.DAY,
    monthHelper: translations?.IDENTITY_INFO_FORM_HELP_TEXT?.MONTH,
    yearHelper: translations?.IDENTITY_INFO_FORM_HELP_TEXT?.YEAR,
    phoneNumberHelper:
      !isUnifiedRegistration && translations?.IDENTITY_INFO_FORM_HELP_TEXT?.PHONE_NUMBER,
    memberIDHelper: translations?.IDENTITY_INFO_FORM_HELP_TEXT?.MEMBER_ID
  };
  const emailValue = window.sessionStorage?.getItem('emailValue');
  const [memberIDModalOpen, setMemberIDModalOpen] = useState(false);

  useEffect(() => {
    if (!router?.isReady) {
      return;
    }
    const firstName = getValues('firstName') || personalInfoData?.firstName;
    const lastName = getValues('lastName') || personalInfoData?.lastName;
    const phoneNumber = getValues('phoneNumber');
    setValue('firstName', firstName);
    setValue('lastName', lastName);
    if (!phoneNumber && personalInfoData?.phoneNumber?.[0]) {
      setValue('phoneNumber', formatPhoneNumber(personalInfoData?.phoneNumber[0]?.number));
    }
  }, [router?.isReady, personalInfoData]);

  useEffect(() => {
    const error_keys = Object.keys(errors);
    if (error_keys?.length > 0) {
      formErrorAnalytics(
        error_keys,
        errors,
        analytics?.flowName || getAdobeFlowName(),
        analytics?.pageName || tags.enterYourInformationPage,
        'search',
        undefined,
        englishMessageMap
      );
    }
  }, [errors]);

  return (
    <div className="personal-info-container">
      {!phoneDOB && (
        <>
          {emailValue && (
            <>
              <div className={styles.emailLabel}>
                <p>{translations?.EMAIL_ADDRESS_LABEL}</p>
              </div>
              <div>
                <p className={styles.emailValue}> {emailValue} </p>
              </div>
            </>
          )}
          <Input
            id={styles.firstName}
            autocomplete="given-name"
            label={formLabels.FIRST_NAME}
            helper={formDetails.firstNameHelper}
            error={errors.firstName?.message?.toString()}
            data-test-id="firstName"
            isErrorAnnounced={false}
            {...register('firstName')}
          />
          <Input
            label={formLabels.LAST_NAME}
            autocomplete="family-name"
            helper={formDetails.lastNameHelper}
            error={errors.lastName?.message?.toString()}
            data-test-id="lastName"
            isErrorAnnounced={false}
            {...register('lastName')}
          />
        </>
      )}
      <DateFieldsetWrapper
        refProp={dobInputRef}
        legend={dobLegendLabel}
        helper={translations?.DOB_LABELS?.DATE_OF_BIRTH_HELPER}
        showHideLabel={translations?.DOB_LABELS?.SHOW_DATE_OF_BIRTH}
        error={errors.dateOfBirth?.message?.toString()}
        dataTestId="dateOfBirth"
        register={register}
        required={true}
        isErrorAnnounced={true}
        hasShowHide={dobShowHide}
        isShowHideChecked={dobShowHideChecked}
      />

      <Input
        inputType="tel"
        autocomplete="tel"
        inputPattern="^\\(\\d{3}\\)\\s\\d{3}-\\d{4}"
        inputMaxlength="16"
        id={isUnifiedRegistration ? styles.phoneNumber__UR : styles.phoneNumber}
        className={isUnifiedRegistration ? styles.phoneNumberField__UR : styles.phoneNumberField}
        inputAutocomplete="tel"
        inputMode="numeric"
        onKeyDown={(e) => onlyNumberKey(e)}
        ref={phoneInputRef}
        onInput={(e) => formatTel(e)}
        onBlur={(e) => formatTel(e)}
        label={formLabels.PHONE_NUMBER}
        helper={formDetails.phoneNumberHelper}
        error={errors.phoneNumber?.message?.toString()}
        data-test-id="phoneNumber"
        isErrorAnnounced={false}
        {...register('phoneNumber')}
      />
      {showMemberId && isCaremark() && (
        <>
          <Input
            id={styles.memberID}
            label={formLabels.MEMBER_ID}
            helper={formDetails.memberIDHelper}
            error={errors.memberID?.message?.toString()}
            //inputPattern="{11}"
            data-test-id="memberID"
            isErrorAnnounced={false}
            maxLength={25} //Confirm a maximum length
            {...register('memberID')}
          />
          <MemberIDModal
            memberIDModalOpen={memberIDModalOpen}
            setMemberIDModalOpen={setMemberIDModalOpen}
            translations={translations}
          />
        </>
      )}
    </div>
  );
};

export default PersonalInfoForm;
